import { useMemo, useState } from 'react';
import { t } from 'i18next';
import { isEmpty } from 'lodash';

import useSearchableEventsQuery from '../../../components/api/queries/useSearchableEventsQuery';
import { useQueryParams } from '@pocapan/shared/utils/useQueryParams';

import StickyHeader from '../../../components/StickyHeader';
import EventActivityBox from '../../../components/event/EventActivityBox';
import { ReportFootprintBreadcrumb } from '../../../components/report/ReportFootprintBreadcrumb';
import HorizontalScroll from '../../../presentation/my-page/components/HorizontalScroll';

import { Button, InputPlain } from '@peacecover/component';
import { Search, Xmark } from '@peacecover/icon';
import { ACTIVITIES } from '../../../constants/activities';

const ReportFootprintsSelectActivityPage = () => {
  const [searchInput, setSearchInput] = useState('');
  const { queryParams, updateQueryParams, removeQueryParams } = useQueryParams();
  const [selectedActivity, setSelectedActivity] = useState('all');
  
  const events = useSearchableEventsQuery({
    artistId: queryParams.get('artistId'),
    memberId: queryParams.get('memberId'),
    category: selectedActivity === 'all' ? '' : selectedActivity
  });
  
  const handleSelectActivity = (activity: string) => {
    setSelectedActivity(activity);
    setSearchInput('');
  };

  const eventActivityCategory = (event: any) => {
    const { category } = event;
    return ['lightstick', 'seasons', 'membership'].includes(category) ? 'misc' : category;
  };

  const filteredEvents = useMemo(() => {
    if (isEmpty(events)) return [];
    if (isEmpty(searchInput)) return events;
    const lowerCaseSearchInput = searchInput.toLowerCase();
    return events.filter((event: any) => {
      const eventName = event.name ? event.name.toLowerCase() : '';
      const eventCategoryName = event.categoryName ? event.categoryName.toLowerCase() : '';
      const localizedNames = event.translations?.localizedName || {};

      return (
        eventName.includes(lowerCaseSearchInput) ||
        eventCategoryName.includes(lowerCaseSearchInput) ||
        Object.values(localizedNames).some((name: any) =>
          name?.toLowerCase().includes(lowerCaseSearchInput)
        )
      );
    });
  }, [events, searchInput]);

  const handleSelectEvent = (event: any) => {
    updateQueryParams(
      {
        eventId: event.eventId
      },
      '/report-location/footprints/new'
    );
  };
  
  return (
    <>
      <StickyHeader
        title={
          t('ReportFootprintsSelectActivityPage.pageTitle', {
            defaultValue: '제보하기'
          }) as string
        }
        alwaysShowTitle={true}
        showBackButton={false}
        showTitle={true}
        leftContent={
          <Xmark
            size={24}
            className="text-text-main-lv1"
            onClick={() =>
              removeQueryParams(['contentType'], '/report-location/footprints-category')
            }
          />
        }
      />
      <div className="flex flex-col pt-[calc(54px+env(safe-area-inset-top))] pb-[calc(128px+env(safe-area-inset-bottom))]">
        <div className="py-[10px]">
          <div className="flex flex-col gap-[18px] mx-[24px]">
            <div className="mx-[-24px]">
              <ReportFootprintBreadcrumb />
            </div>
            <h1 className="text-[26px] font-bold text-pure-black dark:text-pure-white leading-none">
              {t('ReportFootprintsSelectActivityPage.title', {
                defaultValue: '어떤 컨텐츠 인가요?'
              })}
            </h1>
          </div>
          <div>
            <HorizontalScroll>
              <div className="flex items-center gap-[6px] mt-[21px] first:ml-[24px] last:mr-[24px]">
                {ACTIVITIES.map((activity) => (
                  <div key={activity.key}>
                    <Button
                      size="small"
                      className="px-[12px] text-[13px] font-medium"
                      btnColor={selectedActivity === activity.key ? 'primary' : 'secondary'}
                      onClick={() => handleSelectActivity(activity.key)}
                    >
                      {t(`EventCategory.${activity.key}.value`)}
                    </Button>
                  </div>
                ))}
              </div>
            </HorizontalScroll>
            <div className="flex items-center px-[16px] py-[15px] rounded-[12px] bg-secondary-light dark:bg-secondary-dark mt-[16px] mx-[24px]">
              <InputPlain
                size="medium"
                className="bg-transparent"
                placeholder={
                  t('ReportFootprintsSelectActivityPage.placeholder', {
                    defaultValue: '내용'
                  }) as string
                }
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <Search size={24} className="text-placeholder-light dark:text-placeholder-dark" />
            </div>
            <div className="flex flex-col gap-[12px] mt-[16px] mx-[24px]">
              {filteredEvents.map((event: any) => (
                <EventActivityBox
                  key={event.eventId}
                  event={event}
                  activityCategory={eventActivityCategory(event)}
                  isShowBadge={false}
                  handleSelectEvent={() => handleSelectEvent(event)}
                  isShowDate={true}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportFootprintsSelectActivityPage;
